import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import { styles as sharedStyles } from '../styles/shared-styles';

// Sticky install nudge.
//
// Mounted at the app shell level (app-index), so it floats above
// whatever route is rendering. Pinned to the bottom of the viewport
// like a toast — visible from anywhere in the app, dismissible per
// session or forever.
//
// Hides itself when ANY of the following is true:
//   - The app is already running in standalone display mode
//     (matchMedia or navigator.standalone for iOS) — it's installed.
//   - The user previously dismissed it with the "X" button — snoozed
//     for SNOOZE_DAYS.
//   - The user picked "Don't remind me" — never_remind flag set, no
//     more nudges until the flag is manually cleared (clearing site
//     data is the only reset path).
//   - The current route is in HIDDEN_ROUTE_PREFIXES — currently
//     `/track/` (public customer-facing order tracking page; those
//     visitors aren't operators).
//
// Install paths picked by device capability:
//   - Chromium-based browsers fire `beforeinstallprompt`. The CTA
//     calls .prompt() directly.
//   - iOS Safari doesn't fire that event (Apple does not implement
//     the W3C install API). CTA opens an instructions sheet with the
//     Share → "Add to Home Screen" steps.
//   - Anything else (desktop Safari, Firefox) → generic instructions.
//
// Why install? Operator-facing wins:
//   - Stays current — every cold launch from the installed shortcut
//     hits NetworkFirst on /index.html and loads the latest build.
//   - Full screen — no address bar pushing content off the viewport.
//   - Works on flaky connections — cached for fast cold start.
const SNOOZE_KEY = 'install-prompt:snoozedAt';
const NEVER_KEY = 'install-prompt:neverRemind';
const SNOOZE_DAYS = 14;
const HIDDEN_ROUTE_PREFIXES = ['/track/'];

type Mode = 'native-prompt' | 'manual-instructions';

// Platform identity for the manual-instructions fallback (when the
// browser doesn't expose beforeinstallprompt and we can't fire a
// native install dialog). The instructions text differs per
// platform — iOS uses the Share sheet, Android uses the 3-dot menu,
// desktop browsers use an address-bar install icon.
type PlatformGuide =
  'ios' | 'android-chromium' | 'desktop-chromium' | 'firefox' | 'unknown';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@customElement('install-prompt-card')
export class InstallPromptCard extends LitElement {
  @state() private _hidden = true;
  @state() private _mode: Mode = 'manual-instructions';
  @state() private _platformGuide: PlatformGuide = 'unknown';
  @state() private _installing = false;
  @state() private _instructionsOpen = false;

  // Stashed beforeinstallprompt event — only fired on browsers that
  // implement it (Chromium-based on Android + desktop). Kept on the
  // instance because the W3C spec only lets each event be used once.
  private _pendingPrompt: BeforeInstallPromptEvent | null = null;

  private _beforeInstallListener = (e: Event) => {
    e.preventDefault();
    this._pendingPrompt = e as BeforeInstallPromptEvent;
    this._mode = 'native-prompt';
    // Re-evaluate visibility now that a native prompt is possible —
    // some browsers fire the event seconds after page load.
    this._refreshVisibility();
  };

  private _appInstalledListener = () => {
    this._hidden = true;
    try {
      localStorage.removeItem(SNOOZE_KEY);
    } catch {
      /* no-op */
    }
  };

  private _routeChangeListener = () => this._refreshVisibility();

  static styles = [
    sharedStyles,
    css`
      :host {
        /* Stays out of layout flow — sits as a fixed overlay. */
        position: fixed;
        z-index: 950; /* above shell, below modal dialogs (Shoelace 1000+) */
        inset: auto 12px 12px 12px;
        display: block;
        pointer-events: none; /* the card itself opts in via .card */
      }
      @media (min-width: 720px) {
        :host {
          inset: auto 16px 16px auto;
          max-width: 380px;
        }
      }
      .card {
        pointer-events: auto;
        position: relative;
        padding: 14px 38px 14px 14px;
        border-radius: var(--sl-border-radius-large);
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        box-shadow: var(--app-shadow-md, 0 8px 24px rgba(0, 0, 0, 0.18));
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 12px;
        animation: slide-up 0.28s ease-out;
      }
      @keyframes slide-up {
        from {
          transform: translateY(110%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      .icon-bubble {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: var(--sl-color-primary-100);
        color: var(--sl-color-primary-700);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        flex-shrink: 0;
      }
      .sl-theme-dark .icon-bubble {
        background: rgba(225, 71, 126, 0.18);
        color: var(--sl-color-primary-300);
      }
      .body {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
      }
      .title {
        font-weight: 700;
        font-family: var(--font-display, system-ui);
        font-size: 0.95rem;
        line-height: 1.2;
      }
      .why {
        font-size: 0.8rem;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        line-height: 1.35;
      }
      .actions {
        margin-top: 4px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
      }
      .never-link {
        background: none;
        border: 0;
        padding: 4px 0;
        font: inherit;
        font-size: 0.78rem;
        color: var(--app-text-muted, var(--sl-color-neutral-500));
        text-decoration: underline;
        cursor: pointer;
      }
      .never-link:hover,
      .never-link:focus-visible {
        color: var(--app-text);
      }
      .close-btn {
        position: absolute;
        top: 6px;
        right: 6px;
      }
      .steps {
        margin: 0;
        padding-left: 1.2rem;
        font-size: 0.95rem;
        line-height: 1.55;
      }
      .steps li {
        margin-bottom: 0.5rem;
      }
      .steps code {
        background: var(--sl-color-neutral-100);
        padding: 1px 6px;
        border-radius: 4px;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 0.85em;
      }
      .sl-theme-dark .steps code {
        background: rgba(255, 255, 255, 0.08);
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this._platformGuide = this._detectPlatform();
    // Mode starts as manual-instructions; if beforeinstallprompt
    // fires (Chromium browsers do this once their engagement
    // heuristic is met), the listener flips it to 'native-prompt'.
    this._mode = 'manual-instructions';
    window.addEventListener('beforeinstallprompt', this._beforeInstallListener);
    window.addEventListener('appinstalled', this._appInstalledListener);
    // Route changes don't fire native popstate when the app uses
    // pushState directly via the SPA router; the router wraps in a
    // custom event. Listen to both so the route-prefix hide works.
    window.addEventListener('popstate', this._routeChangeListener);
    window.addEventListener('route-changed', this._routeChangeListener);
    this._refreshVisibility();
  }

  disconnectedCallback(): void {
    window.removeEventListener(
      'beforeinstallprompt',
      this._beforeInstallListener
    );
    window.removeEventListener('appinstalled', this._appInstalledListener);
    window.removeEventListener('popstate', this._routeChangeListener);
    window.removeEventListener('route-changed', this._routeChangeListener);
    super.disconnectedCallback();
  }

  // Recompute _hidden against every gating condition. Called on
  // connect, on route change, and after a beforeinstallprompt
  // arrives (since natively-installable status can affect the
  // pitch we want to show).
  private _refreshVisibility() {
    if (this._isStandalone()) {
      this._hidden = true;
      return;
    }
    if (this._isNeverRemind()) {
      this._hidden = true;
      return;
    }
    const snoozedUntil = this._snoozedUntilUtcMs();
    if (snoozedUntil !== null && Date.now() < snoozedUntil) {
      this._hidden = true;
      return;
    }
    if (this._isOnHiddenRoute()) {
      this._hidden = true;
      return;
    }
    this._hidden = false;
  }

  private _isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    const mq = window.matchMedia?.('(display-mode: standalone)');
    if (mq?.matches) return true;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return nav.standalone === true;
  }

  // Pick a platform guide from the user agent. Best-effort — UA
  // sniffing is fragile, but the install path genuinely differs
  // per OS+browser combo and there's no feature-detection
  // alternative for "show me where the Install menu item lives".
  // Order matters: iOS check first because iPadOS reports as Mac.
  private _detectPlatform(): PlatformGuide {
    const ua = navigator.userAgent;
    const isIos =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIos) return 'ios'; // all iOS browsers use WebKit, instructions are identical
    if (/Android/.test(ua)) {
      // Firefox on Android doesn't support PWA install at all; lump
      // it into 'firefox' so the dialog is honest about that.
      if (/Firefox/.test(ua)) return 'firefox';
      return 'android-chromium';
    }
    // Desktop platforms.
    if (/Firefox/.test(ua)) return 'firefox';
    if (/Edg|Chrome|Chromium|Brave/.test(ua)) return 'desktop-chromium';
    return 'unknown';
  }

  private _isNeverRemind(): boolean {
    try {
      return localStorage.getItem(NEVER_KEY) === '1';
    } catch {
      return false;
    }
  }

  private _snoozedUntilUtcMs(): number | null {
    try {
      const raw = localStorage.getItem(SNOOZE_KEY);
      if (!raw) return null;
      const at = Number.parseInt(raw, 10);
      if (!Number.isFinite(at)) return null;
      return at + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    } catch {
      return null;
    }
  }

  private _isOnHiddenRoute(): boolean {
    const path = window.location.pathname;
    return HIDDEN_ROUTE_PREFIXES.some((p) => path.startsWith(p));
  }

  private _snooze() {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    } catch {
      /* no-op */
    }
    this._hidden = true;
  }

  private _neverRemind() {
    try {
      localStorage.setItem(NEVER_KEY, '1');
    } catch {
      /* no-op */
    }
    this._hidden = true;
  }

  private async _onInstallTap() {
    if (this._mode === 'native-prompt' && this._pendingPrompt) {
      this._installing = true;
      try {
        await this._pendingPrompt.prompt();
        const choice = await this._pendingPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          this._hidden = true;
        }
        this._pendingPrompt = null;
      } finally {
        this._installing = false;
      }
      return;
    }
    // No native install path available — fall back to the manual
    // instructions dialog. Body content is platform-specific so an
    // Android user doesn't see Safari Share-sheet instructions.
    this._instructionsOpen = true;
  }

  private _renderInstructionsBody() {
    switch (this._platformGuide) {
      case 'ios':
        return html`
          <p>
            Add Pag-asenso to your home screen so it opens full-screen and stays
            current automatically.
          </p>
          <ol class="steps">
            <li>
              Tap the <strong>Share</strong> button at the bottom of Safari (the
              square with an arrow pointing up).
            </li>
            <li>Scroll down and choose <strong>Add to Home Screen</strong>.</li>
            <li>
              Confirm the name <code>Pag-asenso</code> and tap
              <strong>Add</strong>.
            </li>
            <li>Open the new home-screen icon — that's your installed app.</li>
          </ol>
        `;
      case 'android-chromium':
        return html`
          <p>
            Add Pag-asenso to your home screen so it opens full-screen and stays
            current automatically.
          </p>
          <ol class="steps">
            <li>
              Tap the <strong>⋮</strong> menu in the top-right corner of the
              browser.
            </li>
            <li>
              Choose <strong>Install app</strong> (sometimes labelled
              <strong>Add to Home screen</strong>).
            </li>
            <li>Confirm <strong>Install</strong>.</li>
            <li>Open the new home-screen icon — that's your installed app.</li>
          </ol>
          <p style="font-size: 0.85rem; opacity: 0.75; margin-top: 1rem;">
            If you don't see <strong>Install app</strong>, your browser may need
            a few more seconds of activity on this page before it offers the
            option. Scroll around and try again.
          </p>
        `;
      case 'desktop-chromium':
        return html`
          <p>
            Install Pag-asenso as a desktop app so it gets its own window and
            stays current automatically.
          </p>
          <ol class="steps">
            <li>
              Look for the <strong>Install</strong> icon at the right edge of
              the address bar (small screen-with-arrow icon). If you see it,
              click it and confirm.
            </li>
            <li>
              No icon there? Open the browser's <strong>⋮</strong> menu and look
              for <strong>Install Pag-asenso…</strong> (or
              <strong>Apps → Install this site as an app</strong> on Edge).
            </li>
            <li>
              Confirm <strong>Install</strong>. The app launches in its own
              window.
            </li>
          </ol>
        `;
      case 'firefox':
        return html`
          <p>
            Firefox doesn't currently support installing web apps with a single
            click. Two options:
          </p>
          <ol class="steps">
            <li>
              <strong>On phone:</strong> open the browser's menu and choose
              <strong>Add to Home screen</strong>. The shortcut opens in a
              browser tab rather than a standalone window.
            </li>
            <li>
              <strong>For the best experience:</strong> open Pag-asenso in a
              Chromium-based browser (Chrome, Edge, Brave) and install from
              there — Firefox doesn't currently support installing web apps.
            </li>
          </ol>
        `;
      case 'unknown':
      default:
        return html`
          <p>
            We couldn't detect your browser to give exact instructions. Look for
            an <strong>Install</strong>, <strong>Add to Home Screen</strong>, or
            <strong>Install app</strong> option in your browser's menu or
            address bar — most modern browsers offer one of those.
          </p>
        `;
    }
  }

  private _renderInstructionsDialog() {
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Install Pag-asenso"
        ?open=${this._instructionsOpen}
        @sl-request-close=${() => (this._instructionsOpen = false)}
      >
        ${this._renderInstructionsBody()}
        <sl-button
          slot="footer"
          variant="primary"
          @click=${() => (this._instructionsOpen = false)}
        >
          Got it
        </sl-button>
      </sl-dialog>
    `;
  }

  render() {
    if (this._hidden) return nothing;

    const ctaLabel =
      this._mode === 'native-prompt' ? 'Install' : 'How to install';

    return html`
      <div class="card" role="region" aria-label="Install Pag-asenso">
        <sl-icon-button
          class="close-btn"
          name="x-lg"
          label="Hide for two weeks"
          @click=${() => this._snooze()}
        ></sl-icon-button>
        <div class="icon-bubble" aria-hidden="true">
          <sl-icon name="phone"></sl-icon>
        </div>
        <div class="body">
          <div class="title">Install Pag-asenso</div>
          <div class="why">
            Full-screen, always up to date, works even on flaky connections.
          </div>
          <div class="actions">
            <sl-button
              size="small"
              variant="primary"
              ?loading=${this._installing}
              ?disabled=${this._installing}
              @click=${() => this._onInstallTap()}
            >
              ${ctaLabel}
            </sl-button>
            <button
              class="never-link"
              type="button"
              @click=${() => this._neverRemind()}
            >
              Don't remind me again
            </button>
          </div>
        </div>
      </div>
      ${this._renderInstructionsDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'install-prompt-card': InstallPromptCard;
  }
}
